import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, of, tap, forkJoin, map, catchError, switchMap, throwError } from 'rxjs';
import * as Papa from 'papaparse';
import { SENTIMENT_ANALYSIS_CONFIG } from '../../components/sentiment-analysis/sentiment-analysis.config';
import { AppStateService } from '../helpers/app-state.service';

/* =======================
   Interfaces
======================= */

export interface ThemeSentimentBreakdown {
  themeId: string;
  themeName: string;
  /** When true, this row is a suggested (LLM-proposed) theme; default Details view hides these. */
  isSuggestedTheme?: boolean;
  sentiment: {
    positive: { count: number; percentage: number };
    neutral: { count: number; percentage: number };
    negative: { count: number; percentage: number };
  };
}

export interface SubThemeSentimentBreakdown {
  themeId: string;
  subThemeId: string;
  subThemeName: string;
  /** Parent theme is LLM-suggested (`is_suggested_theme` on subtheme_distribution rows). */
  isSuggestedTheme?: boolean;
  /** When true, this row is a suggested sub-theme (`is_suggested_subtheme` from API). */
  isSuggestedSubtheme?: boolean;
  sentiment: {
    positive: { count: number; percentage: number };
    neutral: { count: number; percentage: number };
    negative: { count: number; percentage: number };
  };
}

/** True when the stats row should be treated as “suggested” for UI bucketing (sub-theme and/or parent theme). */
export function isSubThemeDistributionRowSuggested(
  s: Pick<SubThemeSentimentBreakdown, 'isSuggestedSubtheme' | 'isSuggestedTheme'>,
): boolean {
  return s.isSuggestedSubtheme === true || s.isSuggestedTheme === true;
}

export interface ActionableMentionsInsights {
  actionableMentionsTotal: number;
  sentiment: {
    positive: { count: number; percentage: number };
    neutral: { count: number; percentage: number };
    negative: { count: number; percentage: number };
  };
  npsScore: number;
  sentimentScore: number;
  nps?: {
    promoters: { count: number; percentage: number };
    passives: { count: number; percentage: number };
    detractors: { count: number; percentage: number };
  };
}

export interface AllFeedbackInsights {
  allFeedbackTotal?: number;
  sentiment: {
    positive: { count: number; percentage: number };
    neutral: { count: number; percentage: number };
    negative: { count: number; percentage: number };
  };
  npsScore: number;
  sentimentScore: number;
  /** Present for CSAT-type projects (insights overview). */
  csatScore?: number;
  ratingType?: 'nps' | 'csat';
}

/** NPS (promoter / passive / detractor) or CSAT (top-2 / middle / bottom-2) segments for the overview donut. */
export interface RatingOverviewSegment {
  label: string;
  count: number;
  percentage: number;
}

export interface RatingOverview {
  type: 'nps' | 'csat';
  /** NPS net score (−100…100) or CSAT % (top two boxes, 0–100). */
  score: number;
  segments: RatingOverviewSegment[];
}

/** Overview widget `theme_sentiment` counts (theme-level sentiment labels). */
export interface ThemeSentimentOverview {
  positive: { count: number; percentage: number };
  neutral: { count: number; percentage: number };
  negative: { count: number; percentage: number };
}

export interface DashboardStats {
  brandId: string;
  actionableComments: number;
  actionableMentions: number;
  sentiment: {
    positive: { count: number; percentage: number };
    neutral: { count: number; percentage: number };
    negative: { count: number; percentage: number };
  };
  /** From overview API `theme_sentiment` — used by Details tab actionable sentiment cards. */
  themeSentiment?: ThemeSentimentOverview;
  themeDistribution: Array<{ themeId: string; themeName: string; count: number; isSuggestedTheme?: boolean }>;
  themeSentimentBreakdown?: ThemeSentimentBreakdown[];
  subThemeSentimentBreakdown?: SubThemeSentimentBreakdown[];
  actionableThemeSentimentBreakdown?: ThemeSentimentBreakdown[];
  actionableSubThemeSentimentBreakdown?: SubThemeSentimentBreakdown[];
  actionableMentionsInsights?: ActionableMentionsInsights;
  allFeedbackInsights?: AllFeedbackInsights;
  /** Insights overview: rating distribution donut + primary score type (NPS vs CSAT). */
  ratingOverview?: RatingOverview | null;
  lastUpdated?: string;
  createdAt?: string;
}

/** One row of the Cohort-wise break-up table (theme/sub-theme × cohort bucket × sentiment). */
export interface CohortBreakdownRow {
  name: string;
  positive: number;
  neutral: number;
  negative: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/** One row for Summary generator "Top issues" (v2 `summary.top_issues` or legacy `facts.top_subthemes`). */
export interface SummaryTopIssueLine {
  subthemeName: string;
  cohortLabel: string;
  cohortValue: string;
  mentionCount: number;
  /** From LLM `display_text` — shown with `issueSummary` when present. */
  displayText?: string;
  /** From LLM `issue_summary`. */
  issueSummary?: string;
  /** From LLM `actionable_insight`. */
  actionableInsight?: string;
}

/** Payload for generate/get summary (v1 text-only; v2 adds structured blocks). */
export interface InsightsSummaryGenerateData {
  summary: string;
  feedbackCount: number;
  actionableCount: number;
  topIssues?: SummaryTopIssueLine[];
  nextSteps?: string[];
}

export interface InsightsTimeRange {
  /** ISO-8601 start (inclusive). */
  from: string;
  /** ISO-8601 end (inclusive). */
  to: string;
}

/** Pagination metadata from insights raw-comments (`data.page_info`). */
export interface RawCommentsPageInfo {
  page: number;
  page_size: number;
  returned_count: number;
  total_count: number;
  has_more: boolean;
  next_page: number | null;
}

/** Sub-theme entry under a taxonomy theme (matches feedback engine `config.taxonomy.themes[].subthemes[]`). */
export interface TaxonomySubthemePayload {
  name: string;
  description?: string;
  keywords?: string[];
}

/** Theme entry for taxonomy upload (`config.taxonomy.themes[]`). */
export interface TaxonomyThemePayload {
  name: string;
  description?: string;
  keywords?: string[];
  subthemes?: TaxonomySubthemePayload[];
}

export interface TaxonomyUploadPayload {
  mode: 'optional' | 'none' | string;
  themes: TaxonomyThemePayload[];
}

/** Response body from POST `/api/config/v1/taxonomy_upload`. */
export interface TaxonomyUploadApiResponse {
  project_id: string;
  configuration_version: number;
  inserted_id: string;
  taxonomy: {
    mode: string;
    theme_count: number;
    subtheme_count: number;
  };
}

/** Mongo `configurations` document (subset) from GET `/api/config/v1/latest_sentiment_config`. */
export interface LatestSentimentConfigDocument {
  _id?: string;
  component_name?: string;
  version?: number;
  /** Stored as BSON ObjectId; API may return hex string or extended JSON `{ $oid }`. */
  project_id?: string | { $oid: string };
  config?: {
    taxonomy?: {
      mode?: string;
      themes?: TaxonomyThemePayload[];
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface SentimentConfigCheckResult {
  ready: boolean;
  document: LatestSentimentConfigDocument | null;
}

/* =======================
   Service
======================= */

@Injectable({
  providedIn: 'root',
})
export class SentimentAnalysisStatsService {
  private readonly config = SENTIMENT_ANALYSIS_CONFIG;
  private readonly baseUrl = this.config.apiBaseUrl;

  private statsSubject = new BehaviorSubject<DashboardStats | null>(null);
  public stats$ = this.statsSubject.asObservable();

  private statsCache = new Map<string, DashboardStats>();
  private statsLoaded = new Set<string>();
  private sentimentConfigCache = new Map<string, SentimentConfigCheckResult>();

  /** Required when useInsightsEngine: time window for all insights stats POSTs. */
  private insightsTimeRange: InsightsTimeRange | null = null;

  constructor(
    private http: HttpClient,
    private appStateService: AppStateService,
  ) {}

  /** Set calendar range before loading stats (insights engine). */
  setInsightsTimeRange(range: InsightsTimeRange | null): void {
    this.insightsTimeRange = range;
  }

  getInsightsTimeRange(): InsightsTimeRange | null {
    return this.insightsTimeRange;
  }

  private resolveUrl(path: string): string {
    const base = this.baseUrl.replace(/\/$/, '');
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${base}${p}`;
  }

  /**
   * Session cookies apply to the dashboard origin only; cross-origin calls to the insights microservice
   * do not receive them. When enabled, we forward the logged-in manager user_name for the microservice
   * to resolve req.user (TRUST_FORWARDED_USER).
   */
  private getLoggedInManagerUserName(): string | null {
    try {
      const u = Helpers?.managerDetails?.manager?.user_name;
      if (u != null && String(u).trim() !== '') {
        return String(u).trim();
      }
    } catch {
      /* Helpers may be unset before login */
    }
    return null;
  }

  private getInsightsHttpOptions(): { headers?: HttpHeaders; withCredentials: boolean } {
    const opts: { headers?: HttpHeaders; withCredentials: boolean } = { withCredentials: true };
    if (!this.config.insightsForwardUserName) {
      return opts;
    }
    const userName = this.getLoggedInManagerUserName();
    if (userName) {
      opts.headers = new HttpHeaders().set('X-Litmus-User-Name', userName);
    }
    return opts;
  }

  private ensureBrandId(projectId?: string | null): string {
    return String(projectId || '').trim();
  }

  /** Stable cache segment from `AppStateService.hierarchyStack()` (same contract as analytics `hierarchy_stack`). */
  private hierarchyStackCacheSegment(): string {
    try {
      const stack = this.appStateService.hierarchyStack();
      if (!Array.isArray(stack) || !stack.length) {
        return '';
      }
      const parts = stack.map((level: { field_id?: unknown; group_key?: unknown; type?: unknown }) => {
        const fid = String(level?.field_id ?? '').trim();
        const gk = Array.isArray(level?.group_key)
          ? (level.group_key as unknown[]).map((k) => String(k ?? '').trim()).filter(Boolean).join(',')
          : level?.group_key != null
            ? String(level.group_key).trim()
            : '';
        const t = level?.type !== undefined && level?.type !== null ? String(level.type) : '';
        return `${fid}:${gk}:${t}`;
      });
      return parts.join('|');
    } catch {
      return '';
    }
  }

  private insightsCacheKey(brandId: string): string {
    const r = this.insightsTimeRange;
    const h = this.hierarchyStackCacheSegment();
    if (!r) return h ? `${brandId}::${h}` : brandId;
    return h ? `${brandId}::${r.from}::${r.to}::${h}` : `${brandId}::${r.from}::${r.to}`;
  }

  /**
   * From `AppStateService.hierarchyStack()` — same shape as analytics `hierarchy_stack`:
   * `[{ field_id, group_key: string[], type?: number }]`; `type === 2` → manager tag path on API.
   */
  private getInsightsHierarchyStackPayload(): Array<{
    field_id: string;
    group_key: string[];
    type?: number;
  }> | undefined {
    try {
      const stack = this.appStateService.hierarchyStack();
      if (!Array.isArray(stack) || !stack.length) {
        return undefined;
      }
      const normalized = stack
        .map((level: any) => ({
          field_id: String(level?.field_id ?? '').trim(),
          group_key: Array.isArray(level?.group_key)
            ? level.group_key.map((k: unknown) => String(k ?? '').trim()).filter(Boolean)
            : level?.group_key != null
              ? [String(level.group_key).trim()].filter(Boolean)
              : [],
          ...(level?.type !== undefined && level?.type !== null ? { type: Number(level.type) } : {}),
        }))
        .filter((row) => row.field_id && row.group_key.length);
      return normalized.length ? normalized : undefined;
    } catch {
      return undefined;
    }
  }

  private buildInsightsBaseBody(brandId: string, filters: Record<string, unknown> = {}, options: Record<string, unknown> = {}) {
    const r = this.insightsTimeRange;
    if (!r?.from || !r?.to) {
      throw new Error('Insights time range is not set; call setInsightsTimeRange before loading stats.');
    }
    const hierarchy_stack = this.getInsightsHierarchyStackPayload();
    return {
      brand_id: brandId,
      project_id: brandId,
      time: {
        from: r.from,
        to: r.to,
        field: this.config.statsTimeField,
        timezone: this.config.statsTimezone,
      },
      ...(hierarchy_stack?.length ? { hierarchy_stack } : {}),
      filters,
      options,
    };
  }

  /**
   * Maps brand `main_rater_type` to stats `options.rater_type` for POST /api/stats/v1/overview
   * (see `normalizeRaterType` in micro-service-feedback-insights-engine).
   */
  private buildOverviewOptionsFromBrand(): Record<string, unknown> {
    try {
      const raw =
        Helpers?.brandDetails?.main_rater_type ?? Helpers?.brandDetail?.main_rater_type;
      const n = Number(raw);
      if (n === 3 || n === 10 || n === 11) {
        return { rater_type: 'nps' };
      }
      if (n === 4) {
        return { rater_type: 'csat' };
      }
    } catch {
      /* Helpers may be unset before brand context loads */
    }
    return {};
  }

  /**
   * Canonical microservice stats URLs: `POST {prefix}/{widget}` e.g. `/api/stats/v1/raw_comments`.
   * When `insightsStatsPathPrefix` is unset (legacy Litmus engine), uses `POST /v1/stats/{kebab-widget}`.
   */
  private insightsStatsWidgetPath(widgetUnderscore: string): string {
    const prefix = this.config.insightsStatsPathPrefix;
    if (prefix) {
      return `${prefix}/${widgetUnderscore}`;
    }
    const kebab = widgetUnderscore.replace(/_/g, '-');
    return `/v1/stats/${kebab}`;
  }

  /** Heatmap uses the same stats prefix as other widgets (`POST …/v1/heatmap` on the microservice). */
  private insightsStatsHeatmapWidgetPath(): string {
    const prefix = this.config.insightsStatsHeatmapPathPrefix;
    if (prefix) {
      return `${prefix}/heatmap`;
    }
    return `/v1/stats/heatmap`;
  }

  /**
   * Gateway often wraps JSON as `{ status, message, data: { ok, widget, data, meta } }`.
   * Litmus engine returns the widget object at the root. Normalize to the widget shape
   * so `overview.ok` and `overview.data` match mapper expectations.
   */
  private unwrapInsightWidgetEnvelope(raw: any): any {
    if (raw == null) return raw;
    if (raw.ok === true || raw.ok === false) {
      return raw;
    }
    const inner = raw?.data;
    if (inner != null && typeof inner === 'object' && (inner.ok === true || inner.ok === false)) {
      return inner;
    }
    /** e.g. `{ status, data: { data: { items, page_info } } }` with no `ok` on the middle object */
    if (
      inner != null &&
      typeof inner === 'object' &&
      inner.data != null &&
      typeof inner.data === 'object' &&
      (Array.isArray((inner.data as any).items) || (inner.data as any).page_info != null)
    ) {
      return { ok: true, data: inner.data, meta: (inner as any).meta };
    }
    return raw;
  }

  private postInsightWidget(path: string, body: object): Observable<any> {
    return this.http
      .post<any>(this.resolveUrl(path), body, this.getInsightsHttpOptions())
      .pipe(map((raw) => this.unwrapInsightWidgetEnvelope(raw)));
  }

  /**
   * Normalize ES / insights sentiment label to dashboard card colors (legacy feed shape).
   */
  private normalizeInsightSentiment(label: string): 'positive' | 'neutral' | 'negative' {
    const s = String(label ?? '')
      .trim()
      .toLowerCase();
    if (s === 'positive' || s === 'negative' || s === 'neutral') {
      return s;
    }
    return 'neutral';
  }

  /**
   * Map raw-comments API rows to the categorized-feedback shape used by
   * verbatim panels, heatmap drill-down, and cohort breakdown.
   */
  private mapRawCommentRowToLegacyFeedback(row: any): any {
    const rawLabel =
      row?.selected_sentiment?.label ??
      row?.matched_theme?.theme_sentiment?.label ??
      row?.overall_sentiment?.label ??
      '';
    const sentiment = this.normalizeInsightSentiment(String(rawLabel));
    const text = String(row?.comment_text ?? '').trim();
    const created = row?.created_date ?? null;
    const npsRaw = row?.nps_score;
    const npsNum =
      npsRaw !== undefined && npsRaw !== null && String(npsRaw).trim() !== '' ? Number(npsRaw) : NaN;
    const npsScore = Number.isFinite(npsNum) ? npsNum : undefined;
    const lpd =
      row?.litmus_processed_data != null && typeof row.litmus_processed_data === 'object' && !Array.isArray(row.litmus_processed_data)
        ? row.litmus_processed_data
        : {};
    return {
      sentiment,
      primaryComment: text,
      text,
      litmus_processed_data: lpd,
      feedback: {
        primaryComment: text,
        sentiment,
        createdAt: created,
        npsScore,
        litmus_processed_data: lpd,
        age: lpd['age'],
        gender: lpd['gender'],
        interactionType: lpd['interactionType'],
      },
      createdAt: created,
      feed_id: row?.feed_id,
    };
  }

  /**
   * One page of verbatim comments via POST `{insightsStatsPathPrefix}/raw_comments`
   * (e.g. `/api/stats/v1/raw_comments`) when using micro-service-feedback-insights-engine.
   */
  getRawCommentsPage(
    projectId: string,
    filters: { theme_name: string; subtheme_name?: string; sentiment_labels?: string[] },
    page: number
  ): Observable<{
    success: boolean;
    data: any[];
    pageInfo: RawCommentsPageInfo | null;
    error?: string;
  }> {
    const bId = this.ensureBrandId(projectId);
    if (!this.config.useInsightsEngine) {
      return of({ success: true, data: [], pageInfo: null });
    }
    const themeName = String(filters.theme_name ?? '').trim();
    const subName = filters.subtheme_name != null ? String(filters.subtheme_name).trim() : '';
    if (!themeName) {
      return of({ success: true, data: [], pageInfo: null });
    }
    if (filters.subtheme_name != null && !subName) {
      return of({ success: true, data: [], pageInfo: null });
    }

    let body: object;
    try {
      const sentimentLabels = Array.isArray(filters.sentiment_labels)
        ? filters.sentiment_labels.map((s) => String(s).trim()).filter(Boolean)
        : [];
      body = this.buildInsightsBaseBody(
        bId,
        {
          theme_name: themeName,
          ...(subName ? { subtheme_name: subName } : {}),
          ...(sentimentLabels.length ? { sentiment_labels: sentimentLabels } : {}),
        },
        {
          page: Math.max(1, page),
          page_size: this.config.rawCommentsPageSize,
          sentiment_scope: 'theme',
          sort_order: 'desc',
        }
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Insights time range is not set.';
      return of({ success: false, data: [], pageInfo: null, error: msg });
    }

    return this.postInsightWidget(this.insightsStatsWidgetPath('raw_comments'), body).pipe(
      map((raw: any) => {
        const payload = raw?.data?.data ?? raw?.data;
        const items = payload?.items ?? raw?.data?.items;
        // Some gateways omit `ok` on the widget object when wrapping; accept a well-shaped payload.
        const widgetOk =
          raw?.ok === true || (raw?.ok !== false && Array.isArray(items));
        if (!widgetOk) {
          return {
            success: false,
            data: [] as any[],
            pageInfo: null,
            error: 'Raw comments request returned an error',
          };
        }
        const list = Array.isArray(items)
          ? items.map((r: any) => this.mapRawCommentRowToLegacyFeedback(r))
          : [];
        const pi = payload?.page_info ?? raw?.data?.page_info;
        const pageInfo: RawCommentsPageInfo | null =
          pi && typeof pi === 'object'
            ? {
                page: Number(pi.page ?? page),
                page_size: Number(pi.page_size ?? this.config.rawCommentsPageSize),
                returned_count: Number(pi.returned_count ?? list.length),
                total_count: Number(pi.total_count ?? list.length),
                has_more: Boolean(pi.has_more),
                next_page: pi.next_page != null ? Number(pi.next_page) : null,
              }
            : null;
        return { success: true, data: list, pageInfo };
      }),
      catchError((err) =>
        of({
          success: false,
          data: [] as any[],
          pageInfo: null,
          error: String(err?.error?.message ?? err?.message ?? 'Raw comments request failed'),
        })
      )
    );
  }

  /** First page only, `ApiResponse` shape — used where infinite scroll is not wired. */
  private fetchRawCommentsForDashboard(
    brandId: string,
    filters: { theme_name: string; subtheme_name?: string }
  ): Observable<ApiResponse<any[]>> {
    return this.getRawCommentsPage(brandId, filters, 1).pipe(
      map((r) => ({
        success: r.success,
        data: r.data,
        error: r.error,
      }))
    );
  }

  private requireInsightsTimeRange(): InsightsTimeRange {
    const r = this.insightsTimeRange;
    if (!r?.from || !r?.to) {
      throw new Error('Insights time range is not set; call setInsightsTimeRange before summary.');
    }
    return r;
  }

  /**
   * Filters for POST /api/summary/v1/run (on-demand LLM — `themeSummariesRepo.buildCommentsFilter`).
   * `subtheme_name` is omitted when not provided so the summary spans the whole theme.
   */
  private buildInsightsSummaryV1Filters(
    themeId: string,
    themeName: string,
    sentiment: 'positive' | 'neutral' | 'negative' | 'all',
    onlyActionable: boolean,
    subThemeId?: string,
    subThemeName?: string
  ): Record<string, unknown> {
    const tn = String(themeName || themeId || '').trim();
    const sn = String(subThemeName ?? subThemeId ?? '').trim();
    const out: Record<string, unknown> = {
      theme_name: tn,
      only_actionable: onlyActionable,
    };
    if (sentiment && sentiment !== 'all') {
      out['theme_sentiment_label'] = sentiment;
    }
    if (sn) {
      out['subtheme_name'] = sn;
    }
    return out;
  }

  /** Maps Details tab trend period to summary v2 `period_type` (week | month | quarter). */
  private mapDashboardTimePeriodToV2Period(tp: string | undefined | null): 'week' | 'month' | 'quarter' {
    const key = String(tp ?? '').trim().toLowerCase();
    const map: Record<string, 'week' | 'month' | 'quarter'> = {
      weekly: 'week',
      monthly: 'month',
      quarterly: 'quarter',
      daily: 'week',
      yearly: 'quarter',
    };
    return map[key] ?? 'month';
  }

  /**
   * Summary v2 `period_type`: accepts canonical `week` | `month` | `quarter` (e.g. from datePicker `trendType`)
   * or legacy Details strings (`weekly`, `monthly`, …).
   */
  private resolveSummaryV2PeriodType(periodHint: string | undefined | null): 'week' | 'month' | 'quarter' {
    const t = String(periodHint ?? '').trim().toLowerCase();
    if (t === 'week' || t === 'month' || t === 'quarter') {
      return t;
    }
    return this.mapDashboardTimePeriodToV2Period(periodHint);
  }

  /** Body for POST /api/summary/v2/run (`summary/v2/run_post.js`). */
  private buildInsightsSummaryV2Body(
    projectId: string,
    themeName: string,
    sentimentLabel: string,
    periodType: 'week' | 'month' | 'quarter'
  ): Record<string, unknown> {
    const body: Record<string, unknown> = {
      project_id: projectId,
      theme_name: String(themeName ?? '').trim(),
      period_type: periodType,
      sentiment_label: String(sentimentLabel ?? 'all').trim().toLowerCase() || 'all',
      sentiment_mode: 'theme',
      timezone: this.config.statsTimezone,
    };
    body['now'] = new Date();
    return body;
  }

  private mapInsightsSummaryV1Response(res: any): ApiResponse<InsightsSummaryGenerateData> {
    if (res?.claimed === false) {
      return {
        success: false,
        error: String(res?.message ?? 'Summary is already in progress. Try again shortly.'),
      };
    }
    if (res?.ok === false) {
      return {
        success: false,
        error: String(res?.failure?.message ?? res?.failure?.code ?? 'Summary generation failed'),
      };
    }
    if (res?.ok && res?.summary != null) {
      const summaryObj = res.summary;
      const text =
        typeof summaryObj === 'string'
          ? summaryObj
          : String(summaryObj?.summary_text ?? '').trim();
      return {
        success: true,
        data: {
          summary: text,
          feedbackCount: Number(res.matched_comment_count ?? 0),
          actionableCount: Number(res.sampled_comment_count ?? 0),
        },
      };
    }
    return { success: false, error: 'Unexpected summary response' };
  }

  private mapInsightsSummaryV2Response(res: any): ApiResponse<InsightsSummaryGenerateData> {
    if (res?.ok === false && res?.status === 'not_ready') {
      return {
        success: false,
        error: String(
          res?.message ??
            'Precomputed summary is not available/ready for the selected window',
        ),
      };
    }
    if (res?.ok === false) {
      return {
        success: false,
        error: String(res?.message ?? res?.failure?.message ?? 'Summary request failed'),
      };
    }
    if (res?.ok === true) {
      const summaryObj = res.summary;
      const text =
        summaryObj == null
          ? ''
          : typeof summaryObj === 'string'
            ? summaryObj
            : String(summaryObj?.summary_text ?? '').trim();
      const { topIssues, nextSteps } = this.parseInsightsSummaryV2FactsAndFocusAreas(res, summaryObj);
      return {
        success: true,
        data: {
          summary: text,
          feedbackCount: Number(res.matched_comment_count ?? 0),
          actionableCount: Number(res.sampled_comment_count ?? 0),
          ...(topIssues.length ? { topIssues } : {}),
          ...(nextSteps.length ? { nextSteps } : {}),
        },
      };
    }
    return { success: false, error: 'Unexpected summary response' };
  }

  /**
   * v2 run response: `summary.top_issues` → Top issues (fallback: `facts.top_subthemes`);
   * `summary.next_steps` → Next steps (fallback: `summary.focus_areas`).
   */
  private parseInsightsSummaryV2FactsAndFocusAreas(
    res: any,
    summaryObj: any
  ): { topIssues: SummaryTopIssueLine[]; nextSteps: string[] } {
    const topIssues: SummaryTopIssueLine[] = [];
    const summaryTopIssues =
      summaryObj != null && typeof summaryObj === 'object' && Array.isArray((summaryObj as any).top_issues)
        ? ((summaryObj as any).top_issues as any[])
        : null;

    if (summaryTopIssues?.length) {
      for (const row of summaryTopIssues) {
        const displayText = String(row?.display_text ?? '').trim();
        const issueSummary = String(row?.issue_summary ?? '').trim();
        const actionableInsight = String(row?.actionable_insight ?? '').trim();
        const subthemeName = String(row?.subtheme_name ?? '').trim();
        if (!displayText && !issueSummary && !subthemeName) continue;
        const cf = row?.top_cohort_fact;
        const cohortLabel =
          cf != null && cf.cohort_label != null ? String(cf.cohort_label).trim() : '';
        const cohortValue =
          cf != null && cf.cohort_value != null ? String(cf.cohort_value).trim() : '';
        const mentionCount =
          cf != null && cf.mention_count != null
            ? Number(cf.mention_count)
            : Number(row?.mention_count ?? 0);
        topIssues.push({
          subthemeName: subthemeName || displayText || issueSummary,
          cohortLabel,
          cohortValue,
          mentionCount,
          ...(displayText ? { displayText } : {}),
          ...(issueSummary ? { issueSummary } : {}),
          ...(actionableInsight ? { actionableInsight } : {}),
        });
      }
    }

    if (!topIssues.length) {
      const raw = res?.facts?.top_subthemes;
      if (Array.isArray(raw)) {
        for (const row of raw) {
          const subthemeName = String(row?.subtheme_name ?? '').trim();
          if (!subthemeName) continue;
          const cf = row?.top_cohort_fact;
          const cohortLabel =
            cf != null && cf.cohort_label != null ? String(cf.cohort_label).trim() : '';
          const cohortValue =
            cf != null && cf.cohort_value != null ? String(cf.cohort_value).trim() : '';
          const mentionCount =
            cf != null && cf.mention_count != null
              ? Number(cf.mention_count)
              : Number(row?.mention_count ?? 0);
          topIssues.push({ subthemeName, cohortLabel, cohortValue, mentionCount });
        }
      }
    }

    const nextRaw =
      summaryObj != null && typeof summaryObj === 'object' && Array.isArray((summaryObj as any).next_steps)
        ? ((summaryObj as any).next_steps as unknown[])
        : null;
    let nextSteps = nextRaw?.length
      ? nextRaw.map((x: unknown) => String(x ?? '').trim()).filter(Boolean)
      : [];
    if (!nextSteps.length) {
      const fa =
        summaryObj != null && typeof summaryObj === 'object' ? (summaryObj as any).focus_areas : null;
      nextSteps = Array.isArray(fa)
        ? fa.map((x: unknown) => String(x ?? '').trim()).filter(Boolean)
        : [];
    }
    return { topIssues, nextSteps };
  }

  private postInsightsSummary(
    body: Record<string, unknown>,
    mapResponse: (raw: any) => ApiResponse<InsightsSummaryGenerateData>
  ): Observable<ApiResponse<InsightsSummaryGenerateData>> {
    return this.postInsightWidget(this.config.insightsSummaryRunPath, body).pipe(
      map((raw) => mapResponse(raw)),
      catchError((err) =>
        of({
          success: false,
          error: String(err?.error?.message ?? err?.message ?? 'Summary request failed'),
        })
      )
    );
  }

  private runInsightsSummaryV1(
    projectId: string,
    filters: Record<string, unknown>,
    force: boolean
  ): Observable<ApiResponse<{ summary: string; feedbackCount: number; actionableCount: number }>> {
    let r: InsightsTimeRange;
    try {
      r = this.requireInsightsTimeRange();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Insights time range is not set.';
      return of({ success: false, error: msg });
    }
    const body = {
      project_id: projectId,
      from_ts: r.from,
      to_ts: r.to,
      filters,
      force,
    };
    return this.postInsightsSummary(body, (raw) => this.mapInsightsSummaryV1Response(raw));
  }

  private runInsightsSummaryV2(
    projectId: string,
    themeName: string,
    sentimentLabel: string,
    periodHint?: string
  ): Observable<ApiResponse<{ summary: string; feedbackCount: number; actionableCount: number }>> {
    const periodType = this.resolveSummaryV2PeriodType(periodHint);
    const body = this.buildInsightsSummaryV2Body(projectId, themeName, sentimentLabel, periodType);
    return this.postInsightsSummary(body, (raw) => this.mapInsightsSummaryV2Response(raw));
  }

  private pct(count: number, total: number): { count: number; percentage: number } {
    if (!total) return { count: count || 0, percentage: 0 };
    return { count, percentage: Math.round((count / total) * 10000) / 100 };
  }

  private mapInsightsToDashboardStats(brandId: string, overview: any, themeDist: any, subthemeDist: any): DashboardStats {
    const od = overview?.data ?? {};
    const pos = od.overall_sentiment?.positive ?? 0;
    const neg = od.overall_sentiment?.negative ?? 0;
    const neu = od.overall_sentiment?.neutral ?? 0;
    const mix = od.overall_sentiment?.mixed ?? 0;
    const totalSent = pos + neg + neu + mix;

    const tsPos = Number(od.theme_sentiment?.positive ?? 0);
    const tsNeg = Number(od.theme_sentiment?.negative ?? 0);
    const tsNeu = Number(od.theme_sentiment?.neutral ?? 0);
    const tsMix = Number(od.theme_sentiment?.mixed ?? 0);
    const totalThemeSent = tsPos + tsNeg + tsNeu + tsMix;
    const themeSentiment: ThemeSentimentOverview = {
      positive: this.pct(tsPos, totalThemeSent),
      neutral: this.pct(tsNeu, totalThemeSent),
      negative: this.pct(tsNeg, totalThemeSent),
    };

    const themeBuckets = themeDist?.data?.buckets ?? [];

    const rawSuggested = (b: any): boolean => {
      const v = b?.is_suggested_theme ?? b?.isSuggestedTheme;
      if (v === true || v === 1) return true;
      if (typeof v === 'string' && v.toLowerCase() === 'true') return true;
      return false;
    };

    const rawSuggestedSubtheme = (b: any): boolean => {
      const v = b?.is_suggested_subtheme ?? b?.isSuggestedSubtheme;
      if (v === true || v === 1) return true;
      if (typeof v === 'string' && v.toLowerCase() === 'true') return true;
      return false;
    };

    const themeDistribution = themeBuckets.map((b: any) => ({
      themeId: String(b.theme_name ?? ''),
      themeName: String(b.theme_name ?? ''),
      count: Number(b.count ?? 0),
      isSuggestedTheme: rawSuggested(b),
    }));

    const mapThemeRow = (b: any): ThemeSentimentBreakdown => {
      const p = Number(b.positive ?? 0);
      const n = Number(b.neutral ?? 0);
      const ng = Number(b.negative ?? 0);
      const t = p + n + ng || 1;
      const name = String(b.theme_name ?? '');
      return {
        themeId: name,
        themeName: name,
        isSuggestedTheme: rawSuggested(b),
        sentiment: {
          positive: this.pct(p, t),
          neutral: this.pct(n, t),
          negative: this.pct(ng, t),
        },
      };
    };

    const themeSentimentBreakdown = themeBuckets.map(mapThemeRow);

    const subBuckets = subthemeDist?.data?.buckets ?? [];
    const subThemeSentimentBreakdown: SubThemeSentimentBreakdown[] = subBuckets.map((b: any) => {
      const p = Number(b.positive ?? 0);
      const n = Number(b.neutral ?? 0);
      const ng = Number(b.negative ?? 0);
      const t = p + n + ng || 1;
      const themeName = String(b.theme_name ?? '');
      const subName = String(b.subtheme_name ?? '');
      return {
        themeId: themeName,
        subThemeId: subName,
        subThemeName: subName,
        isSuggestedTheme: rawSuggested(b),
        isSuggestedSubtheme: rawSuggestedSubtheme(b),
        sentiment: {
          positive: this.pct(p, t),
          neutral: this.pct(n, t),
          negative: this.pct(ng, t),
        },
      };
    });

    const prom = Number(od.nps_buckets?.promoter ?? 0);
    const pas = Number(od.nps_buckets?.passive ?? 0);
    const det = Number(od.nps_buckets?.detractor ?? 0);
    const npsTotal = prom + pas + det;
    const npsScoreComputed = npsTotal > 0 ? Math.round(((prom - det) / npsTotal) * 100) : 0;
    const npsScoreRaw = Number(od.nps_score ?? od.rating_summary?.nps_score);
    const npsScore = Number.isFinite(npsScoreRaw) ? Math.round(npsScoreRaw) : npsScoreComputed;

    const ratingTypeRaw = String(od.rating_type ?? od.rating_summary?.type ?? 'nps').toLowerCase();
    const ratingType: 'nps' | 'csat' = ratingTypeRaw === 'csat' ? 'csat' : 'nps';

    const t2 = Number(od.csat_buckets?.top_2_box ?? 0);
    const midCsat = Number(od.csat_buckets?.middle_bucket ?? 0);
    const b2 = Number(od.csat_buckets?.bottom_2_box ?? 0);
    const csatTotal = t2 + midCsat + b2;
    const csatScoreRaw = Number(od.csat_score ?? od.rating_summary?.csat_score);
    const csatScore = Number.isFinite(csatScoreRaw)
      ? Math.round(csatScoreRaw * 100) / 100
      : csatTotal > 0
        ? Math.round((t2 / csatTotal) * 10000) / 100
        : 0;

    let ratingOverview: RatingOverview | null = null;
    if (ratingType === 'csat') {
      const segments: RatingOverviewSegment[] = [
        {
          label: 'T2B%',
          count: t2,
          percentage: csatTotal > 0 ? this.pct(t2, csatTotal).percentage : 0,
        },
        {
          label: 'MB%',
          count: midCsat,
          percentage: csatTotal > 0 ? this.pct(midCsat, csatTotal).percentage : 0,
        },
        {
          label: 'B2B%',
          count: b2,
          percentage: csatTotal > 0 ? this.pct(b2, csatTotal).percentage : 0,
        },
      ];
      ratingOverview = { type: 'csat', score: csatScore, segments };
    } else {
      const segments: RatingOverviewSegment[] = [
        {
          label: 'Promoters (9–10)',
          count: prom,
          percentage: npsTotal > 0 ? this.pct(prom, npsTotal).percentage : 0,
        },
        {
          label: 'Passives (7–8)',
          count: pas,
          percentage: npsTotal > 0 ? this.pct(pas, npsTotal).percentage : 0,
        },
        {
          label: 'Detractors (0–6)',
          count: det,
          percentage: npsTotal > 0 ? this.pct(det, npsTotal).percentage : 0,
        },
      ];
      const npsScoreDisplay = Number.isFinite(npsScoreRaw) ? Math.round(npsScoreRaw * 100) / 100 : npsScore;
      ratingOverview = { type: 'nps', score: npsScoreDisplay, segments };
    }

    const sentimentBlock = {
      positive: this.pct(pos, totalSent),
      neutral: this.pct(neu, totalSent),
      negative: this.pct(neg, totalSent),
    };

    /** Net sentiment index: positive share minus negative share of classified feedback (−100…100). */
    const sentimentScore =
      totalSent > 0 ? Math.round(((pos - neg) / totalSent) * 100) : 0;

    const actionableCommentsCount = Number(od.actionable_comment_count ?? 0);
    const actionableMentionsCount = Number(od.actionable_mention_count ?? 0);
    const actionableFromSplit = actionableCommentsCount + actionableMentionsCount;
    const actionableCountAgg = Number(od.actionable_count ?? 0);
    const actionableTotal =
      actionableFromSplit > 0 ? actionableFromSplit : actionableCountAgg;

    return {
      brandId,
      actionableComments: actionableCommentsCount,
      actionableMentions: actionableMentionsCount,
      sentiment: sentimentBlock,
      themeSentiment,
      themeDistribution,
      themeSentimentBreakdown,
      subThemeSentimentBreakdown,
      actionableThemeSentimentBreakdown: themeSentimentBreakdown,
      actionableSubThemeSentimentBreakdown: subThemeSentimentBreakdown,
      actionableMentionsInsights: {
        actionableMentionsTotal: actionableTotal,
        sentiment: sentimentBlock,
        npsScore: ratingType === 'csat' ? 0 : npsScore,
        sentimentScore,
        nps: {
          promoters: this.pct(prom, npsTotal),
          passives: this.pct(pas, npsTotal),
          detractors: this.pct(det, npsTotal),
        },
      },
      allFeedbackInsights: {
        allFeedbackTotal: Number(od.total_comments ?? 0),
        sentiment: sentimentBlock,
        npsScore: ratingType === 'csat' ? 0 : npsScore,
        sentimentScore,
        csatScore: ratingType === 'csat' ? csatScore : undefined,
        ratingType,
      },
      ratingOverview,
      lastUpdated: overview?.meta?.generated_at,
    };
  }

  private loadInsightsDashboard(brandId: string): Observable<ApiResponse<DashboardStats>> {
    const overviewBody = this.buildInsightsBaseBody(
      brandId,
      {},
      this.buildOverviewOptionsFromBrand(),
    );
    const themeBody = this.buildInsightsBaseBody(brandId, {}, { top_n: 50, sentiment_scope: 'all' });
    const subBody = this.buildInsightsBaseBody(brandId, {}, { top_n: 50, sentiment_scope: 'all' });

    const emptyBuckets = { ok: true, data: { buckets: [] } };

    return forkJoin({
      overview: this.postInsightWidget(this.insightsStatsWidgetPath('overview'), overviewBody),
      theme: this.postInsightWidget(this.insightsStatsWidgetPath('theme_distribution'), themeBody).pipe(
        catchError(() => of(emptyBuckets))
      ),
      subtheme: this.postInsightWidget(this.insightsStatsWidgetPath('subtheme_distribution'), subBody).pipe(
        catchError(() => of(emptyBuckets))
      ),
    }).pipe(
      map(({ overview, theme, subtheme }) => {
        if (!overview?.ok) {
          return { success: false, error: 'Insights stats overview returned an error payload' };
        }
        const themeOk = theme?.ok ? theme : emptyBuckets;
        const subOk = subtheme?.ok ? subtheme : emptyBuckets;
        const data = this.mapInsightsToDashboardStats(brandId, overview, themeOk, subOk);
        return { success: true, data };
      })
    );
  }

  getStats(projectId: string): Observable<ApiResponse<DashboardStats>> {
    const bId = this.ensureBrandId(projectId);
    if (this.config.useInsightsEngine) {
      return this.loadInsightsDashboard(bId);
    }
    return this.http.get<ApiResponse<DashboardStats>>(this.resolveUrl(this.config.api.statsById(bId)), {
      withCredentials: true,
    });
  }

  loadStatsOnce(projectId: string, options?: { force?: boolean }): Observable<ApiResponse<DashboardStats>> {
    const bId = this.ensureBrandId(projectId);
    const cacheKey = this.config.useInsightsEngine ? this.insightsCacheKey(bId) : bId;

    if (!options?.force && this.statsLoaded.has(cacheKey) && this.statsCache.has(cacheKey)) {
      const cached = this.statsCache.get(cacheKey)!;
      this.statsSubject.next(cached);
      return of({ success: true, data: cached });
    }

    const req = this.config.useInsightsEngine
      ? this.loadInsightsDashboard(bId)
      : this.http.get<ApiResponse<DashboardStats>>(this.resolveUrl(this.config.api.statsById(bId)), { withCredentials: true });

    return req.pipe(
      tap((response) => {
        if (response?.success && response?.data) {
          this.statsCache.set(cacheKey, response.data!);
          this.statsLoaded.add(cacheKey);
          this.statsSubject.next(response.data);
        }
      })
    );
  }

  clearStatsCache(projectId?: string): void {
    if (this.config.useInsightsEngine) {
      if (projectId) {
        const prefix = this.ensureBrandId(projectId);
        for (const k of [...this.statsCache.keys()]) {
          if (k === prefix || k.startsWith(`${prefix}::`)) {
            this.statsCache.delete(k);
            this.statsLoaded.delete(k);
          }
        }
      } else {
        this.statsCache.clear();
        this.statsLoaded.clear();
      }
      return;
    }
    if (projectId) {
      this.statsCache.delete(projectId);
      this.statsLoaded.delete(projectId);
    } else {
      this.statsCache.clear();
      this.statsLoaded.clear();
    }
  }

  getCategorizedFeedbackByTheme(projectId: string, themeId: string): Observable<ApiResponse<any[]>> {
    const bId = this.ensureBrandId(projectId);
    if (this.config.useInsightsEngine) {
      const themeName = String(themeId ?? '').trim();
      if (!themeName) {
        return of({ success: true, data: [] });
      }
      return this.fetchRawCommentsForDashboard(bId, { theme_name: themeName });
    }
    return this.http.get<ApiResponse<any[]>>(
      this.resolveUrl(this.config.api.feedbackByTheme(bId, themeId)),
      { withCredentials: true }
    );
  }

  getCategorizedFeedbackBySubTheme(
    projectId: string,
    themeId: string,
    subThemeId: string
  ): Observable<ApiResponse<any[]>> {
    const bId = this.ensureBrandId(projectId);
    if (this.config.useInsightsEngine) {
      const themeName = String(themeId ?? '').trim();
      const subthemeName = String(subThemeId ?? '').trim();
      if (!themeName || !subthemeName) {
        return of({ success: true, data: [] });
      }
      return this.fetchRawCommentsForDashboard(bId, {
        theme_name: themeName,
        subtheme_name: subthemeName,
      });
    }
    return this.http.get<ApiResponse<any[]>>(
      this.resolveUrl(this.config.api.feedbackBySubTheme(bId, themeId, subThemeId)),
      { withCredentials: true }
    );
  }

  private mapTimePeriodToInterval(timePeriod: string): string {
    switch (timePeriod) {
      case 'daily':
        return 'day';
      case 'weekly':
        return 'week';
      case 'monthly':
        return 'month';
      case 'quarterly':
        return 'quarter';
      case 'yearly':
        return 'year';
      default:
        return 'week';
    }
  }

  getTrendData(
    projectId: string,
    themeId: string | null,
    subThemeId: string | null,
    timePeriod: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'
  ): Observable<ApiResponse<Array<{ period: string; count: number }>>> {
    const bId = this.ensureBrandId(projectId);
    if (this.config.useInsightsEngine) {
      const interval = this.mapTimePeriodToInterval(timePeriod);
      const filters: Record<string, unknown> = {};
      if (themeId) filters['theme_names'] = [themeId];
      if (subThemeId) filters['subtheme_names'] = [subThemeId];
      const path = this.insightsStatsWidgetPath(
        subThemeId && themeId ? 'subtheme_trend' : 'theme_trend'
      );
      const body = this.buildInsightsBaseBody(bId, filters, { interval, series_limit: 12 });
      return this.postInsightWidget(path, body).pipe(
        map((res: any) => {
          if (!res?.ok || !res?.data?.series) {
            return { success: false, data: [] };
          }
          const series = res.data.series as Array<{ key?: string; theme_name?: string; subtheme_name?: string; points?: Array<{ ts: string; count: number }> }>;
          let picked = series;
          if (themeId && !subThemeId) {
            picked = series.filter((s) => s.key === themeId || s.theme_name === themeId);
          }
          if (themeId && subThemeId) {
            const want = `${themeId} > ${subThemeId}`;
            picked = series.filter((s) => s.key === want || (s.theme_name === themeId && s.subtheme_name === subThemeId));
          }
          const byTs = new Map<string, number>();
          for (const s of picked.length ? picked : series) {
            for (const pt of s.points ?? []) {
              const label = String(pt.ts ?? '').replace('T', ' ').slice(0, 16);
              byTs.set(label, (byTs.get(label) ?? 0) + Number(pt.count ?? 0));
            }
          }
          const data = [...byTs.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([period, count]) => ({ period, count }));
          return { success: true, data };
        })
      );
    }
    return this.http.post<ApiResponse<Array<{ period: string; count: number }>>>(
      this.resolveUrl(this.config.api.trend),
      { brandId: bId, themeId, subThemeId, timePeriod },
      { withCredentials: true }
    );
  }

  /**
   * Map legacy UI cohort ids to `stats.heatmap_v2.cohorts` keys on the insights engine.
   */
  private heatmapInsightsCohortKey(cohortType: string): string {
    const m: Record<string, string> = {
      ageGroup: 'age_group',
      gender: 'gender',
      npsCategory: 'nps_category',
      interactionType: 'interaction_type',
    };
    const k = String(cohortType ?? '').trim();
    return m[k] ?? k;
  }

  /**
   * ES heatmap widget returns `{ data: { level, cells, x_labels, y_labels } }` (no `rows`).
   * Map cells to the row shape expected by details-heatmap-widget (themeName + cohortValue + counts).
   * When the API uses cohort × theme (x = cohort bucket, y = theme/subtheme), rows/columns are swapped vs theme × sentiment.
   */
  private mapInsightHeatmapCellsToWidgetRows(
    cells: any[],
    /** Requested UI level; v2 responses omit `data.level`, so we trust the request. */
    level: 'theme' | 'subTheme',
    axes: { x_axis?: { dimension?: string }; y_axis?: { dimension?: string } } | null,
    themeFilterId: string | null
  ): any[] {
    if (!Array.isArray(cells)) return [];
    const xDim = String(axes?.x_axis?.dimension ?? '').toLowerCase();
    const yDim = String(axes?.y_axis?.dimension ?? '').toLowerCase();
    const cohortOnX =
      (xDim === 'cohort' && (yDim === 'theme' || yDim === 'subtheme')) ||
      (xDim === 'rating_bucket' && (yDim === 'theme' || yDim === 'subtheme'));

    return cells.map((c) => {
      const count = Math.max(0, Number(c?.count ?? 0));
      let y = String(c?.y ?? '').trim();
      let x = String(c?.x ?? '').trim();
      const themeName = String(c?.theme_name ?? '').trim();
      const subthemeName = String(c?.subtheme_name ?? '').trim();

      if (cohortOnX) {
        const cohortVal = x;
        const rowKey = y;
        if (level === 'subTheme') {
          const parent = String(themeFilterId ?? '').trim();
          return {
            themeId: parent,
            themeName: parent,
            subThemeName: rowKey,
            subThemeId: rowKey,
            dimensionValue: rowKey,
            cohortValue: cohortVal,
            sentimentCount: count,
            total: count,
          };
        }
        return {
          themeId: rowKey,
          themeName: rowKey,
          cohortValue: cohortVal,
          sentimentCount: count,
          total: count,
        };
      }

      if (level === 'subTheme') {
        const tid = themeName || (x.includes('>') ? x.split('>')[0].trim() : x);
        const stn = subthemeName || (x.includes('>') ? x.split('>')[1]?.trim() ?? '' : '');
        return {
          themeId: tid,
          themeName: tid,
          subThemeName: stn || x,
          subThemeId: stn || x,
          dimensionValue: x,
          cohortValue: y,
          sentimentCount: count,
          total: count,
        };
      }
      return {
        themeId: themeName || x,
        themeName: themeName || x,
        cohortValue: y,
        sentimentCount: count,
        total: count,
      };
    });
  }

  getHeatmapData(
    projectId: string,
    sentimentType: 'negative' | 'non_negative' = 'negative',
    cohortType: string = '',
    level: 'theme' | 'subTheme' = 'theme',
    themeId: string | null = null
  ): Observable<ApiResponse<any[]>> {
    const bId = this.ensureBrandId(projectId);
    const cohort = String(cohortType ?? '').trim();
    if (!cohort) {
      return of({ success: true, data: [] });
    }
    if (this.config.useInsightsEngine) {
      const filters: Record<string, unknown> = {};
      if (themeId && themeId !== 'all') filters['theme_names'] = [themeId];
      /** Heatmap lists only non-suggested themes / sub-themes (matches Details break-up default). */
      if (level === 'theme') {
        filters['is_suggested_theme'] = false;
      } else {
        filters['is_suggested_subtheme'] = false;
      }
      const esLevel = level === 'subTheme' ? 'subtheme' : 'theme';
      const heatmapSentiment =
        sentimentType === 'non_negative' ? 'non_negative' : 'negative';
      const axisOptions: Record<string, unknown> = {
        ...this.buildOverviewOptionsFromBrand(),
        ...(cohort === 'rating_bucket'
          ? {
              x_axis: { dimension: 'rating_bucket' },
              y_axis: { dimension: esLevel },
              heatmap_sentiment_type: heatmapSentiment,
            }
          : {
              x_axis: { dimension: 'cohort', cohort_key: this.heatmapInsightsCohortKey(cohort) },
              y_axis: { dimension: esLevel },
              heatmap_sentiment_type: heatmapSentiment,
            }),
      };
      const body = this.buildInsightsBaseBody(bId, filters, axisOptions);
      return this.postInsightWidget(this.insightsStatsHeatmapWidgetPath(), body).pipe(
        map((res: any) => {
          if (!res?.ok) {
            return { success: true, data: [] };
          }
          const payload = res?.data;
          if (payload == null || typeof payload !== 'object') {
            return { success: true, data: [] };
          }
          const legacyRows = (payload as any).rows;
          if (Array.isArray(legacyRows) && legacyRows.length > 0) {
            return { success: true, data: legacyRows };
          }
          const cells = (payload as any).cells;
          if (Array.isArray(cells) && cells.length > 0) {
            const axes = {
              x_axis: (payload as any).x_axis,
              y_axis: (payload as any).y_axis,
            };
            const mapped = this.mapInsightHeatmapCellsToWidgetRows(
              cells,
              level,
              axes,
              themeId && themeId !== 'all' ? themeId : null
            );
            return { success: true, data: mapped };
          }
          return { success: true, data: [] };
        })
      );
    }
    let params = new HttpParams()
      .set('sentimentType', sentimentType)
      .set('cohortType', cohort)
      .set('level', level);
    if (themeId && themeId !== 'all') {
      params = params.set('themeId', themeId);
    }
    return this.http.get<ApiResponse<any[]>>(this.resolveUrl(this.config.api.heatmap(bId)), {
      params,
      withCredentials: true,
    });
  }

  /**
   * Insights engine: POST `{insightsStatsPathPrefix}/cohort_breakdown` — aggregated positive / neutral / negative
   * counts per cohort bucket for the selected theme (and optional sub-theme).
   */
  getCohortBreakdown(
    projectId: string,
    cohortKey: string,
    themeName: string,
    subthemeName: string | null,
  ): Observable<ApiResponse<CohortBreakdownRow[]>> {
    const bId = this.ensureBrandId(projectId);
    const cohort = String(cohortKey ?? '').trim();
    const theme = String(themeName ?? '').trim();
    if (!this.config.useInsightsEngine || !cohort || !theme) {
      return of({ success: true, data: [] });
    }
    const sub = subthemeName != null ? String(subthemeName).trim() : '';
    let body: object;
    try {
      body = this.buildInsightsBaseBody(
        bId,
        {
          theme_names: [theme],
          ...(sub ? { subtheme_names: [sub] } : {}),
        },
        {
          cohort_key: cohort,
          ...this.buildOverviewOptionsFromBrand(),
        },
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Insights time range is not set.';
      return of({ success: false, data: [], error: msg });
    }
    return this.postInsightWidget(this.insightsStatsWidgetPath('cohort_breakdown'), body).pipe(
      map((raw: any) => {
        if (raw?.ok === false) {
          return {
            success: false,
            data: [] as CohortBreakdownRow[],
            error: 'Cohort breakdown request returned an error',
          };
        }
        const inner = raw?.data?.rows ?? raw?.data?.data?.rows;
        const rows = Array.isArray(inner) ? inner : [];
        return { success: true, data: rows as CohortBreakdownRow[] };
      }),
    );
  }

  /**
   * Insights engine: POST `insightsSummaryRunPath`.
   * - Precomputed (v2): `summary/v2/run_post.js` — `theme_name`, `period_type`, `sentiment_label`, `now` as client `new Date()` (ISO in JSON).
   * - On-demand (v1): `summary/v1/run_post.js` — `from_ts` / `to_ts` / `filters` / `force: true`.
   *
   * `v2PeriodHint`: global datePicker `trendType` (`day` | `week` | `month` | `quarter` | …) resolved to v2 `period_type`,
   * or canonical `week`/`month`/`quarter`, or Details-style `weekly`/`monthly`/`quarterly`. Ignored for v1.
   */
  generateSummary(
    projectId: string,
    themeId: string,
    themeName: string,
    sentiment: 'positive' | 'neutral' | 'negative' | 'all',
    onlyActionable: boolean = true,
    v2PeriodHint?: string,
    subThemeId?: string,
    subThemeName?: string
  ): Observable<ApiResponse<InsightsSummaryGenerateData>> {
    const bId = this.ensureBrandId(projectId);
    if (this.config.useInsightsEngine) {
      if (this.config.insightsSummaryIsPrecomputed) {
        return this.runInsightsSummaryV2(bId, String(themeName || themeId).trim(), sentiment, v2PeriodHint);
      }
      const filters = this.buildInsightsSummaryV1Filters(
        themeId,
        themeName,
        sentiment,
        onlyActionable,
        subThemeId,
        subThemeName
      );
      return this.runInsightsSummaryV1(bId, filters, true);
    }
    return this.http.post<ApiResponse<InsightsSummaryGenerateData>>(
      this.resolveUrl(this.config.api.summaryGenerate),
      {
        brandId: bId,
        themeId,
        subThemeId: subThemeId ?? '',
        themeName,
        subThemeName: subThemeName ?? '',
        sentiment,
        onlyActionable,
      },
      { withCredentials: true }
    );
  }

  /**
   * Insights: v1 uses cached run with `force: false`; v2 is always a precomputed lookup (same POST shape as generate).
   */
  getSummary(
    projectId: string,
    themeId: string,
    sentiment: 'positive' | 'neutral' | 'negative' | 'all',
    onlyActionable: boolean = true,
    themeName?: string,
    v2PeriodHint?: string,
    subThemeId?: string,
    subThemeName?: string
  ): Observable<ApiResponse<InsightsSummaryGenerateData>> {
    const bId = this.ensureBrandId(projectId);
    if (this.config.useInsightsEngine) {
      if (this.config.insightsSummaryIsPrecomputed) {
        return this.runInsightsSummaryV2(
          bId,
          String((themeName ?? themeId) || '').trim(),
          sentiment,
          v2PeriodHint
        );
      }
      const filters = this.buildInsightsSummaryV1Filters(
        themeId,
        themeName ?? themeId,
        sentiment,
        onlyActionable,
        subThemeId,
        subThemeName
      );
      return this.runInsightsSummaryV1(bId, filters, false);
    }
    const params = new HttpParams()
      .set('brandId', bId)
      .set('themeId', themeId)
      .set('subThemeId', subThemeId ?? '')
      .set('sentiment', sentiment);
    return this.http.get<ApiResponse<any>>(this.resolveUrl(this.config.api.summaryGet), {
      params,
      withCredentials: true,
    });
  }

  /**
   * Group CSV-style rows by Theme → `config.taxonomy` (sub-themes under each theme; description/keywords optional).
   *
   * Supported shapes:
   * - **Typed rows** (column `Type`): `theme` applies Description/keywords to the theme; `subtheme` applies them to
   *   the row’s Sub-theme under that Theme (Sub-theme name required).
   * - **Legacy rows** (no `Type` or unrecognized value): same as before — one sub-theme per row; Sub-theme required
   *   to add an entry; Description/keywords attach to that sub-theme.
   *
   * Theme and sub-theme names are normalized to Title Case (each word capitalized,
   * remaining characters lowercased), matching the server-side normalization in
   * `taxonomy_upload_post.js`. This keeps the preview shown in the settings panel
   * consistent with what is persisted, and dedupes inputs that differ only in casing.
   */
  buildTaxonomyFromCsvRows(rows: Record<string, unknown>[]): TaxonomyUploadPayload {
    const cell = (row: Record<string, unknown>, ...keys: string[]) => {
      for (const k of keys) {
        const direct = row[k];
        if (direct !== undefined && direct !== null && String(direct).trim() !== '') {
          return String(direct).trim();
        }
      }
      const lowerKeys = new Map(Object.keys(row).map((x) => [x.trim().toLowerCase(), x]));
      for (const k of keys) {
        const orig = lowerKeys.get(k.toLowerCase());
        if (orig) {
          const v = row[orig];
          if (v !== undefined && v !== null && String(v).trim() !== '') {
            return String(v).trim();
          }
        }
      }
      return '';
    };

    const parseKeywords = (raw: string) =>
      raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

    const normalizeRowType = (raw: string): 'theme' | 'subtheme' | '' => {
      const t = raw.trim().toLowerCase();
      if (t === 'theme') return 'theme';
      if (t === 'subtheme' || t === 'sub-theme' || t === 'sub_theme') return 'subtheme';
      return '';
    };

    const byTheme = new Map<string, TaxonomyThemePayload>();
    const ensureTheme = (themeName: string): TaxonomyThemePayload => {
      let t = byTheme.get(themeName);
      if (!t) {
        t = { name: themeName, subthemes: [] };
        byTheme.set(themeName, t);
      }
      if (!t.subthemes) t.subthemes = [];
      return t;
    };

    for (const row of rows) {
      if (!row || typeof row !== 'object') continue;
      const r = row as Record<string, unknown>;
      const themeName = SentimentAnalysisStatsService.formatTaxonomyName(cell(r, 'Theme', 'theme'));
      if (!themeName) continue;

      const typeRaw = cell(r, 'Type', 'type');
      const rowType = typeRaw ? normalizeRowType(typeRaw) : '';

      const subName = SentimentAnalysisStatsService.formatTaxonomyName(
        cell(r, 'Sub-theme', 'Sub-Theme', 'sub_theme', 'subtheme', 'Subtheme')
      );
      const desc = cell(r, 'Description', 'description');
      const kwCell = cell(r, 'keywords', 'Keywords');
      const keywords = kwCell ? parseKeywords(kwCell) : [];

      const t = ensureTheme(themeName);

      if (rowType === 'theme') {
        if (desc) t.description = desc;
        if (keywords.length) t.keywords = keywords;
        continue;
      }

      if (rowType === 'subtheme') {
        if (!subName) continue;
        const st: TaxonomySubthemePayload = { name: subName };
        if (desc) st.description = desc;
        if (keywords.length) st.keywords = keywords;
        t.subthemes!.push(st);
        continue;
      }

      if (!subName) continue;
      const st: TaxonomySubthemePayload = { name: subName };
      if (desc) st.description = desc;
      if (keywords.length) st.keywords = keywords;
      t.subthemes!.push(st);
    }

    const themes = [...byTheme.values()].filter((t) => t.name);
    return {
      mode: themes.length ? 'optional' : 'none',
      themes,
    };
  }

  /**
   * Title-case helper for taxonomy theme/sub-theme names.
   * Mirrors the server-side `formatTaxonomyName` in `taxonomy_upload_post.js`.
   * Words are delimited by whitespace or "/", so:
   *   "  product  QUALITY " -> "Product Quality"
   *   "renewal communication/reminders" -> "Renewal Communication/Reminders"
   */
  static formatTaxonomyName(value: unknown): string {
    const s = String(value ?? '').trim().replace(/\s+/g, ' ');
    if (!s) return '';
    return s.toLowerCase().replace(/(^|[\s/])(\S)/g, (_m, sep, ch) => sep + ch.toUpperCase());
  }

  private parseTaxonomyCsvFile(file: File): Observable<Record<string, unknown>[]> {
    return new Observable((subscriber) => {
      Papa.parse<Record<string, unknown>>(file, {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (results) => {
          const errs = results.errors ?? [];
          if (errs.length && errs[0]?.code !== 'UndetectableDelimiter') {
            subscriber.error(new Error(errs.map((e) => e.message || String(e)).join('; ') || 'CSV parse failed'));
            return;
          }
          const rows = (results.data ?? []).filter((row) =>
            Object.values(row).some((v) => v !== undefined && v !== null && String(v).trim() !== '')
          );
          subscriber.next(rows);
          subscriber.complete();
        },
        error: (e) => subscriber.error(e),
      });
    });
  }

  clearSentimentConfigCache(brandId?: string): void {
    if (brandId) {
      this.sentimentConfigCache.delete(this.ensureBrandId(brandId));
    } else {
      this.sentimentConfigCache.clear();
    }
  }

  /** True when latest configuration has project taxonomy (at least one theme). */
  isSentimentConfigReady(doc: LatestSentimentConfigDocument | null | undefined): boolean {
    const cfg = doc?.config;
    if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) {
      return false;
    }
    const taxonomy = (cfg as Record<string, unknown>)['taxonomy'] as
      | { themes?: unknown[] }
      | undefined;
    const themes = taxonomy?.themes;
    return Array.isArray(themes) && themes.length > 0;
  }

  /**
   * Accept routeUtils (`status: 0`) and gateway-wrapped bodies (`status: 200`, nested `data.status: 0`).
   */
  private isInsightsConfigHttpBodyOk(body: Record<string, unknown> | null): boolean {
    if (!body) {
      return true;
    }
    const topStatus = Number(body['status']);
    if (topStatus === 0 || topStatus === 200) {
      return true;
    }
    const data = body['data'];
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const inner = data as Record<string, unknown>;
      const innerStatus = Number(inner['status']);
      if (innerStatus === 0 || inner['success'] === true) {
        return true;
      }
    }
    return false;
  }

  /** Extract configuration Mongo document from single- or double-wrapped API bodies. */
  private unwrapLatestSentimentConfigBody(body: Record<string, unknown> | null): LatestSentimentConfigDocument | null {
    if (!body || typeof body !== 'object') {
      return null;
    }

    const isConfigDocument = (value: unknown): value is LatestSentimentConfigDocument => {
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
      }
      const cfg = (value as Record<string, unknown>)['config'];
      return Boolean(cfg && typeof cfg === 'object' && !Array.isArray(cfg));
    };

    if (isConfigDocument(body)) {
      return body;
    }

    let node: unknown = body;
    for (let depth = 0; depth < 6; depth += 1) {
      if (!node || typeof node !== 'object' || Array.isArray(node)) {
        break;
      }
      const layer = node as Record<string, unknown>;

      if (isConfigDocument(layer['response'])) {
        return layer['response'];
      }
      if (isConfigDocument(layer)) {
        return layer;
      }

      const next = layer['data'] ?? layer['response'];
      if (next === node) {
        break;
      }
      node = next;
    }

    return null;
  }

  /**
   * GET latest sentiment configuration for a brand (insights engine).
   * 404 / missing taxonomy → `{ ready: false }`.
   */
  getLatestSentimentConfig(brandId: string, opts?: { force?: boolean }): Observable<SentimentConfigCheckResult> {
    const bId = this.ensureBrandId(brandId);
    if (!bId) {
      return of({ ready: false, document: null });
    }

    const path = this.config.insightsLatestSentimentConfigPath;
    if (!this.config.useInsightsEngine || !path) {
      return of({ ready: true, document: null });
    }

    if (!opts?.force && this.sentimentConfigCache.has(bId)) {
      return of(this.sentimentConfigCache.get(bId)!);
    }

    const url = this.resolveUrl(path);
    const params = new HttpParams().set('brand_id', bId);
    const httpOpts = { ...this.getInsightsHttpOptions(), params, observe: 'response' as const };

    return this.http.get<Record<string, unknown>>(url, httpOpts).pipe(
      map((resp) => {
        const body = (resp.body as Record<string, unknown> | null) ?? null;
        const businessOk = this.isInsightsConfigHttpBodyOk(body);
        if (resp.status >= 200 && resp.status < 300 && businessOk) {
          const document = this.unwrapLatestSentimentConfigBody(body);
          const result: SentimentConfigCheckResult = {
            ready: this.isSentimentConfigReady(document),
            document,
          };
          this.sentimentConfigCache.set(bId, result);
          return result;
        }
        const miss: SentimentConfigCheckResult = { ready: false, document: null };
        this.sentimentConfigCache.set(bId, miss);
        return miss;
      }),
      catchError((err) => {
        if (err?.status === 404) {
          const miss: SentimentConfigCheckResult = { ready: false, document: null };
          this.sentimentConfigCache.set(bId, miss);
          return of(miss);
        }
        return throwError(() => err);
      })
    );
  }

  /** Parse CSV locally into the taxonomy payload that would be uploaded (preview). */
  previewTaxonomyFromCsvFile(file: File): Observable<TaxonomyUploadPayload> {
    return this.parseTaxonomyCsvFile(file).pipe(map((rows) => this.buildTaxonomyFromCsvRows(rows)));
  }

  /**
   * POST merged taxonomy to insights engine (new `configurations` row, version +1).
   */
  /**
   * POST merged project config (`project_config_save_post.js`). Creates a new configuration version.
   */
  saveProjectConfig(
    projectId: string,
    config: Record<string, unknown>,
  ): Observable<TaxonomyUploadApiResponse> {
    const bId = this.ensureBrandId(projectId);
    const path = this.config.insightsProjectConfigSavePath;
    if (!this.config.useInsightsEngine || !path) {
      return throwError(
        () =>
          new Error(
            'Insights engine project config save is not enabled (useInsightsEngine / insightsProjectConfigSavePath).',
          ),
      );
    }
    const url = this.resolveUrl(path);
    const opts = this.getInsightsHttpOptions();
    return this.http
      .post<Record<string, unknown>>(url, { brand_id: bId, config }, { ...opts, observe: 'response' })
      .pipe(map((resp) => this.mapConfigSaveResponseBody(resp.body, bId)));
  }

  private mapConfigSaveResponseBody(
    b: Record<string, unknown> | null,
    fallbackBrandId: string,
  ): TaxonomyUploadApiResponse {
    if (!b || typeof b !== 'object') {
      throw new Error('Empty response from project config save');
    }
    const topStatus = Number(b['status']);
    const businessOk = topStatus === 0 || topStatus === 200;
    if (!businessOk) {
      const msg = String(b['message'] || 'Project config save failed');
      throw new Error(msg);
    }

    const document = this.unwrapLatestSentimentConfigBody(b);
    const version = Number(document?.version ?? 0);
    const themes = document?.config?.taxonomy?.themes ?? [];
    const themeCount = Array.isArray(themes) ? themes.length : 0;
    const subthemeCount = Array.isArray(themes)
      ? themes.reduce(
          (acc, t) =>
            acc +
            (Array.isArray((t as { subthemes?: unknown[] }).subthemes)
              ? (t as { subthemes: unknown[] }).subthemes.length
              : 0),
          0,
        )
      : 0;

    const rawProjectId = document?.['project_id'];
    const storedProjectId =
      rawProjectId != null
        ? String(
            typeof rawProjectId === 'object' && rawProjectId !== null && '$oid' in rawProjectId
              ? rawProjectId.$oid
              : rawProjectId,
          )
        : fallbackBrandId;

    return {
      project_id: storedProjectId,
      configuration_version: version,
      inserted_id: String(document?._id ?? ''),
      taxonomy: {
        mode: String(document?.config?.taxonomy?.mode ?? 'optional'),
        theme_count: themeCount,
        subtheme_count: subthemeCount,
      },
    };
  }

  uploadProjectTaxonomy(projectId: string, taxonomy: TaxonomyUploadPayload): Observable<TaxonomyUploadApiResponse> {
    const bId = this.ensureBrandId(projectId);
    const path = this.config.insightsTaxonomyUploadPath;
    if (!this.config.useInsightsEngine || !path) {
      return throwError(
        () => new Error('Insights engine taxonomy upload is not enabled (useInsightsEngine / insightsStatsPathPrefix).')
      );
    }
    const url = this.resolveUrl(path);
    const opts = this.getInsightsHttpOptions();
    return this.http.post<Record<string, unknown>>(
      url,
      { brand_id: bId, taxonomy },
      { ...opts, observe: 'response' }
    ).pipe(
      map((resp) => {
        const b = resp.body as Record<string, unknown> | null;
        if (!b || typeof b !== 'object') {
          throw new Error('Empty response from taxonomy upload');
        }
        const topStatus = Number(b['status']);
        const businessOk = topStatus === 0 || topStatus === 200;
        if (!businessOk) {
          const msg = String(b['message'] || 'Taxonomy upload failed');
          throw new Error(msg);
        }

        const document = this.unwrapLatestSentimentConfigBody(b);
        const version = Number(document?.version ?? 0);
        const themes = document?.config?.taxonomy?.themes ?? [];
        const themeCount = Array.isArray(themes) ? themes.length : 0;
        const subthemeCount = Array.isArray(themes)
          ? themes.reduce(
              (acc, t) =>
                acc + (Array.isArray((t as { subthemes?: unknown[] }).subthemes) ? (t as { subthemes: unknown[] }).subthemes.length : 0),
              0,
            )
          : 0;

        const rawProjectId = document?.['project_id'];
        const storedProjectId =
          rawProjectId != null
            ? String(
                typeof rawProjectId === 'object' && rawProjectId !== null && '$oid' in rawProjectId
                  ? rawProjectId.$oid
                  : rawProjectId,
              )
            : bId;

        return {
          project_id: storedProjectId,
          configuration_version: version,
          inserted_id: String(document?._id ?? ''),
          taxonomy: {
            mode: String(document?.config?.taxonomy?.mode ?? 'optional'),
            theme_count: themeCount,
            subtheme_count: subthemeCount,
          },
        } satisfies TaxonomyUploadApiResponse;
      })
    );
  }

  /** Parse CSV (Theme, optional Type, Sub-theme, Description, keywords) and POST taxonomy in one step. */
  uploadProjectTaxonomyFromCsvFile(projectId: string, file: File): Observable<TaxonomyUploadApiResponse> {
    return this.parseTaxonomyCsvFile(file).pipe(
      switchMap((rows) => {
        const taxonomy = this.buildTaxonomyFromCsvRows(rows);
        if (!taxonomy.themes.length) {
          return throwError(
            () =>
              new Error(
                'No valid rows found. Expected Theme; optional Type (theme | subtheme); Sub-theme required for subtheme/legacy rows; Description and keywords optional.'
              )
          );
        }
        return this.uploadProjectTaxonomy(projectId, taxonomy);
      })
    );
  }
}
